import {
  Autocomplete,
  Box,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import Input from "../Common/Input";
import Button from "../Common/Button";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  addLesson,
  updateLesson,
  getVideosForLesson,
  addOrgLesson,
  updateOrgLesson,
  getOrgVideosForLesson,
} from "../../store/course/course.action";
import CloseIcon from "@mui/icons-material/Close";
import { uploadFile } from "../../util/AwsFunction";
import { closeDialogue } from "../../util/Dialogue";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  borderRadius: "13px",
  border: "1px solid #C9C9C9",
  boxShadow: 24,
  p: "19px",
};

const CreateLesson = ({
  courseId,
  isEditing = false,
  editingLessonData = null,
  onEditSuccess,
  onAddSuccess,
}) => {
  const dispatch = useDispatch();
  const { tokenData } = useSelector((state) => state.loggedUser);
  const { dialogue } = useSelector((state) => state.dialogue);
  const { selectedCourse } = useSelector((state) => state.course);

  // Autocomplete state for video selection
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");

  const [title, setTitle] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [duration, setDuration] = useState();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [files, setFiles] = useState([]);

  const [errors, setError] = useState({
    title: "",
    videoUrl: "",
    files: [],
  });

  const closePopup = () => {
    closeDialogue(dispatch);
    onEditSuccess();
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    try {
      const { resDataUrl } = await uploadFile(file, `lessonFiles/${courseId}`);
      const newFile = {
        url: resDataUrl,
        name: file.name,
        type: file.type.includes("image")
          ? "image"
          : file.type.includes("pdf")
            ? "pdf"
            : "application/octet-stream",
        id: Date.now().toString(),
      };
      setFiles((prev) => [...prev, newFile]);
    } catch (error) {
      console.error("Error uploading file:", error);
      setError((prev) => ({
        ...prev,
        fileUpload: "Failed to upload file. Please try again.",
      }));
    }

    event.target.value = null;
  };

  // Handle file name change
  const handleFileNameChange = (index, newName) => {
    setFiles((prev) =>
      prev.map((file, idx) =>
        idx === index ? { ...file, name: newName } : file,
      ),
    );
  };

  // Remove a file
  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = () => {
    if (
      !title ||
      !videoUrl ||
      !videoTitle ||
      !files.every((file) => file.name)
    ) {
      let error = { files: [] };
      if (!title) error.title = "Title is required !";
      if (!videoUrl) {
        error.videoUrl = "Video Link is required !";
      } else {
        if (!videoTitle) error.videoUrl = "Video Title is missing !";
      }
      if (!files.every((file) => file.name)) {
        let errors = [];
        files.forEach((file, index) => {
          if (!file.name) {
            errors[index] = "File name is required";
          }
        });
        error.files = errors;
      }
      return setError({ ...error });
    } else {
      const payload = {
        title,
        video: {
          id: selectedVideo?.id || editingLessonData?.video?.id,
          title: videoTitle,
          url: videoUrl,
          thumbnail: thumbnail,
        },
        duration: Number(duration || 0),
        fileUrls: files.map(({ url, name, type }) => ({
          url,
          name,
          type,
        })),
      };

      if (isEditing) {
        if (tokenData.isOrg) {
          dispatch(updateOrgLesson(courseId, editingLessonData._id, payload));
        } else {
          dispatch(updateLesson(courseId, editingLessonData._id, payload));
        }
      } else {
        if (tokenData.isOrg) {
          dispatch(addOrgLesson(courseId, payload)).then((resp) =>
            onAddSuccess(resp),
          );
        } else {
          dispatch(addLesson(courseId, payload)).then((resp) =>
            onAddSuccess(resp),
          );
        }
      }

      closePopup();
    }
  };

  const fetchVideos = () => {
    setLoading(true);
    getVideosForLesson(searchValue, selectedCourse.language)
      .then((data) => {
        // Expecting an array; map to { id, title, url } shape as needed
        const videos = Array.isArray(data)
          ? data.map((item) => ({
              id: item._id,
              uniqueId: item.uniqueVideoId,
              title: item.title || "Untitled",
              url: item.videoUrl,
              thumbnail: item.thumbnail || item.videoImage,
              duration: Math.ceil(item.videoTime / (1000 * 60)),
            }))
          : [];

        setOptions(videos);
      })
      .catch(() => {
        setOptions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchOrgVideo = () => {
    getOrgVideosForLesson(searchValue, selectedCourse._id)
      .then((data) => {
        // Expecting an array; map to { id, title, url } shape as needed
        const videos = Array.isArray(data)
          ? data.map((item) => ({
              id: item._id,
              uniqueId: item.uniqueVideoId,
              title: item.title || "Untitled",
              url: item.videoUrl,
              thumbnail: item.thumbnail || item.videoImage,
              duration: Math.ceil(item.videoTime / (1000 * 60)),
            }))
          : [];

        setOptions(videos);
      })
      .catch(() => {
        setOptions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!tokenData) return;

    const timer = setTimeout(() => {
      if (tokenData.isOrg) {
        fetchOrgVideo();
      } else {
        fetchVideos();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue, selectedCourse, tokenData]);

  useEffect(() => {
    if (isEditing && editingLessonData) {
      setTitle(editingLessonData.title);
      setVideoTitle(editingLessonData.video.title);
      setVideoUrl(editingLessonData.video.url);
      setThumbnail(editingLessonData.video.thumbnail);
      setDuration(editingLessonData.duration);
      setSelectedVideo({
        id: editingLessonData.video.id,
        title: editingLessonData.video.title,
        url: editingLessonData.video.url,
      });
      setFiles(editingLessonData.fileUrls);
    }
  }, [isEditing, editingLessonData]);

  return (
    <Modal
      open={dialogue}
      onClose={closePopup}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} className="create-course-modal">
        <Typography id="modal-modal-title" variant="h6" component="h2">
          Add Lesson
        </Typography>
        <form>
          <div className="row gap-3">
            <div className="col-12">
              <Input
                label={"Title"}
                name={"title"}
                type={"text"}
                placeholder={"Enter Title.."}
                errorMessage={errors.title && errors.title}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);

                  if (!e.target.value) {
                    return setError({
                      ...errors,
                      title: "Title can't be a blank!",
                    });
                  } else {
                    return setError({
                      ...errors,
                      title: "",
                    });
                  }
                }}
              />
            </div>
            <div className="col-12">
              <div className="custom-input">
                <label>Select Video</label>
                <Autocomplete
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  noOptionsText={`No video found with ${selectedCourse.language} language`}
                  value={selectedVideo}
                  onChange={(event, newValue) => {
                    setSelectedVideo(newValue);
                    if (newValue && newValue.url) {
                      setVideoUrl(newValue.url);
                      setVideoTitle(newValue.title);
                      setThumbnail(newValue.thumbnail);
                      setDuration(newValue.duration);
                      if (!newValue.title.trim()) {
                        setError({
                          ...errors,
                          videoUrl: "Video Title is missing !",
                        });
                      } else {
                        setError({
                          ...errors,
                          videoUrl: "",
                        });
                      }
                    } else {
                      setVideoUrl("");
                      setVideoTitle("");
                      setThumbnail("");
                      setDuration("");
                      setError({
                        ...errors,
                        videoUrl: "Video is required !",
                      });
                    }
                  }}
                  inputValue={searchValue}
                  onInputChange={(event, newInputValue) => {
                    setSearchValue(newInputValue);
                  }}
                  // isOptionEqualToValue={(option, value) =>
                  //   option?.title === value?.title
                  // }
                  getOptionLabel={(option) => option?.title}
                  filterOptions={(options) => options}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={option?.uniqueVideoId} {...optionProps}>
                        <div className="w-100 d-flex justify-content-between align-items-center">
                          <span>{option?.title}</span>
                          {option?.thumbnail ? (
                            <img
                              src={option?.thumbnail}
                              className="rounded-2"
                              height={50}
                              alt="thumbnail_image"
                            />
                          ) : (
                            <video
                              className="rounded-2"
                              src={option?.url}
                              height={50}
                            />
                          )}
                        </div>
                      </li>
                    );
                  }}
                  options={options}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select Video" />
                  )}
                />
                {errors.videoUrl && (
                  <p className="errorMessage">{errors.videoUrl}</p>
                )}
              </div>
            </div>
            <div className="col-12">
              <Input
                label={"Duration (Min)"}
                name={"duration"}
                type={"number"}
                placeholder={"Duration (in Min).."}
                errorMessage={errors.duration && errors.duration}
                value={duration}
                disabled
                onChange={(e) => {
                  setDuration(e.target.value);

                  if (!e.target.value) {
                    return setError({
                      ...errors,
                      duration: "Duration can't be a blank!",
                    });
                  } else {
                    return setError({
                      ...errors,
                      duration: "",
                    });
                  }
                }}
              />
            </div>
            <div className="col-12">
              <Input
                type={"file"}
                label={"Supporting File"}
                accept={"image/png, image/jpeg, .pdf"}
                onChange={handleFileUpload}
              />
            </div>
            <div className="col-12">
              {files.map((file, index) => {
                return (
                  <>
                    <div className="d-flex gap-2 mt-3">
                      <TextField
                        placeholder="File Name"
                        variant="standard"
                        value={file.name}
                        onChange={(event) => {
                          handleFileNameChange(index, event.target.value);
                        }}
                        fullWidth
                      />

                      <IconButton
                        onClick={() => {
                          handleRemoveFile(index);
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                    {errors.files[index] ? (
                      <span className="errorMessage">
                        {errors.files[index]}
                      </span>
                    ) : (
                      ""
                    )}
                  </>
                );
              })}
            </div>
          </div>
          <div className="text-secondary my-4">
            <span>Note : </span>
            {selectedCourse.privacy === "org_private"
              ? "Lesson in private course can be created by private and public video."
              : "Lesson in public course can be created by public videos only."}
          </div>

          <div className="mt-3 d-flex justify-content-end gap-2">
            <Button type="button" className={"close"} onClick={closePopup}>
              Cancel
            </Button>
            <Button type="button" className={"submit"} onClick={handleSubmit}>
              {isEditing ? "Update" : "Submit"}
            </Button>
          </div>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateLesson;
